import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useShare } from '../../context/ShareContext';
import supabase from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const formatDateTime = (value) => value
  ? new Date(value).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })
  : 'Noch nie';

const formatDuration = (milliseconds) => {
  const value = Number(milliseconds);
  if (!Number.isFinite(value) || value < 0) return '–';
  if (value < 1000) return `${Math.round(value)} ms`;
  return `${(value / 1000).toFixed(value < 10_000 ? 1 : 0)} s`;
};

const MenuSourceHealthBanner = () => {
  const location = useLocation();
  const { user, dbCommercialOffers = [], providers = [] } = useShare();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);

  const visible = location.pathname === '/admin/menu-sources'
    && ['admin', 'moderator'].includes(user?.role);

  useEffect(() => {
    if (!visible) return undefined;

    let active = true;
    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('commercial_offer_sources')
        .select('offer_id,source_page_url,auto_sync,last_status,last_error,last_attempt_at,last_success_at,next_retry_at,last_duration_ms,consecutive_failures')
        .order('last_attempt_at', { ascending: false, nullsFirst: false });

      if (active) {
        if (error) console.warn('Quellenstatus konnte nicht geladen werden:', error);
        setSources(error ? [] : data || []);
        setLoading(false);
      }
    };

    load();
    const interval = window.setInterval(load, 60_000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [visible]);

  const offerById = useMemo(
    () => new Map(dbCommercialOffers.map((offer) => [String(offer.id), offer])),
    [dbCommercialOffers],
  );
  const providerById = useMemo(
    () => new Map(providers.map((provider) => [String(provider.id), provider])),
    [providers],
  );

  const summary = useMemo(() => {
    const activeSources = sources.filter((source) => source.auto_sync !== false);
    const errors = activeSources.filter((source) => source.last_status === 'error');
    const checking = activeSources.filter((source) => source.last_status === 'checking');
    const healthy = activeSources.filter((source) => ['updated', 'unchanged'].includes(source.last_status));
    return { active: activeSources.length, errors, checking: checking.length, healthy: healthy.length };
  }, [sources]);

  if (!visible) return null;

  return (
    <section className="mx-auto mt-6 w-full max-w-7xl px-4">
      <div className={`rounded-2xl border p-4 shadow-sm ${summary.errors.length ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`rounded-xl p-3 ${summary.errors.length ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
              <SafeIcon icon={summary.errors.length ? FiIcons.FiAlertTriangle : FiIcons.FiActivity} />
            </div>
            <div>
              <h2 className="font-black text-gray-900">Systemstatus der Quellen</h2>
              <p className="text-xs text-gray-600">
                {loading ? 'Status wird aktualisiert…' : `${summary.healthy} gesund · ${summary.checking} läuft · ${summary.errors.length} mit Fehler · ${summary.active} aktiv`}
              </p>
            </div>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-black ${summary.errors.length ? 'bg-amber-200 text-amber-900' : 'bg-emerald-200 text-emerald-900'}`}>
            {summary.errors.length ? 'Aufmerksamkeit erforderlich' : 'Alle Automatiken stabil'}
          </span>
        </div>

        {summary.errors.length > 0 && (
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {summary.errors.map((source) => {
              const offer = offerById.get(String(source.offer_id));
              const provider = providerById.get(String(offer?.provider_id));
              const name = provider?.name || offer?.provider_name || offer?.title || 'Unbekannte Quelle';

              return (
                <div key={source.offer_id} className="rounded-xl border border-amber-200 bg-white p-3 text-xs">
                  <div className="flex items-start justify-between gap-3">
                    <strong className="text-gray-900">{name}</strong>
                    <span className="rounded-full bg-red-100 px-2 py-0.5 font-bold text-red-700">
                      {Number(source.consecutive_failures) || 1} Fehler in Folge
                    </span>
                  </div>
                  <p className="mt-2 line-clamp-2 text-red-700">{source.last_error || 'Unbekannter Fehler'}</p>
                  <div className="mt-2 grid gap-1 text-gray-500 sm:grid-cols-2">
                    <span>Letzter Versuch: {formatDateTime(source.last_attempt_at)}</span>
                    <span>Nächster Versuch: {formatDateTime(source.next_retry_at)}</span>
                    <span>Letzter Erfolg: {formatDateTime(source.last_success_at)}</span>
                    <span>Laufzeit: {formatDuration(source.last_duration_ms)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default MenuSourceHealthBanner;
